/**
 * API «Плановые цены» — матрица культура × месяц (AK-Planning).
 *
 * Спека `docs/superpowers/specs/2026-08-06-spp-scenarios-v1.md`
 * («Решения founder» §1) + прототип экрана 3
 * `docs/superpowers/prototypes/2026-08-06-spp-scenarios/index.html`.
 * Бэкенд: `backend/src/ikolhoz/api/v1/sale_prices.py`.
 *
 * Самодостаточный модуль, отдельный от `@/lib/planning/api` (хаб
 * планирования переезжает на v2 в #692/#693 — форма не вплетается в него
 * глубоко, чтобы не конфликтовать).
 *
 * Блок «Логистика по направлениям» из прототипа СЮДА НЕ входит — схема
 * `company_sale_prices` не несёт оси «направление» (см.
 * `docs/superpowers/specs/2026-08-06-sale-prices-form-open-questions.md`).
 * В UI от него осталась read-only плашка без данных и без ввода.
 */
import { api } from '@/lib/api';

export interface PriceCell {
  month: number; // 1..12
  price_no_vat: number | null;
  volume_t: number | null;
  is_manual: boolean;
  price_id: string | null;
}

export interface CultureRow {
  crop_item_id: string;
  name: string;
  product_class_item_id: string | null;
  months: PriceCell[];
  average: number | null;
}

export interface SalePriceMatrix {
  season_id: string;
  year: number;
  farm_id: string | null;
  cultures: CultureRow[];
}

export function getSalePriceMatrix(
  seasonId: string,
  farmId?: string | null,
): Promise<SalePriceMatrix> {
  const qs = farmId ? `?farm_id=${encodeURIComponent(farmId)}` : '';
  return api.get<SalePriceMatrix>(`/api/v1/planning/seasons/${seasonId}/sale-prices${qs}`);
}

export interface PriceCellPatch {
  crop_item_id: string;
  /** 1..12 */
  month: number;
  /** `null` — ячейку очистили: строка цены уходит в архив, в матрице станет пусто. */
  price_no_vat: number | null;
  /**
   * Не прислан — прежний объём сохраняется; прислан `null` — стирается.
   * Различие сервер ловит по факту наличия ключа, не по значению.
   */
  volume_t?: number | null;
}

export interface BulkUpsertResult {
  /** Ячеек записано (цена проставлена или изменена). */
  updated: number;
  /** Ячеек очищено (строка цены ушла в архив). */
  archived: number;
  matrix: SalePriceMatrix;
}

export function bulkUpsertSalePrices(
  seasonId: string,
  cells: PriceCellPatch[],
  farmId?: string | null,
): Promise<BulkUpsertResult> {
  return api.post<BulkUpsertResult>(`/api/v1/planning/seasons/${seasonId}/sale-prices/bulk`, {
    farm_id: farmId ?? null,
    cells,
  });
}

export interface ImportRow {
  crop_name: string;
  months: Record<number, number>;
}

export interface ImportResult {
  matched: number;
  unmatched: string[];
  cells_prefilled: number;
  cells_skipped_manual: number;
  matrix: SalePriceMatrix;
}

export function importSalePrices(
  seasonId: string,
  rows: ImportRow[],
  farmId?: string | null,
): Promise<ImportResult> {
  return api.post<ImportResult>(`/api/v1/planning/seasons/${seasonId}/sale-prices/import`, {
    farm_id: farmId ?? null,
    rows,
  });
}

// ── Представление ────────────────────────────────────────────────────────────

export const MONTH_LABELS_SHORT = [
  'янв', 'фев', 'мар', 'апр', 'май', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
] as const;

/**
 * Разбор ручного ввода цены в ячейке матрицы.
 *
 * Возвращает:
 *   - `number` — валидная цена (запятая как десятичный разделитель, как в
 *     импорте: «24350,5» → 24350.5; пробелы-разделители разрядов режутся);
 *   - `null`   — ячейку очистили (цену за месяц не задают);
 *   - `undefined` — ввод не число / отрицательное: коммитить нельзя.
 *
 * Отдельно от `<Input type="number">`: в number-инпуте браузер отдаёт пустую
 * строку на «24350,5», и ввод дробных руками ломался (вердикт head-web).
 */
// Реализация — общая с бюджетными справочниками и техкартами (AK-704). Была
// СВОЯ копия с собственной регуляркой: два парсера одного формата расходятся
// молча, и правку одного из них никто не переносит во второй. Ре-экспорт,
// чтобы страница продолжала импортировать парсер «из своего» модуля.
export { parsePriceInput } from './money';

/** Средняя по фактически введённым месяцам (живой пересчёт на фронте — спека §1). */
export function computeAverage(months: PriceCell[]): number | null {
  const values = months.map((m) => m.price_no_vat).filter((v): v is number => v != null);
  if (values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return Math.round((sum / values.length) * 100) / 100;
}
